import React, { useState } from "react";
import SourceChip from "../section/SourceChip.jsx";
import api from "../../api/client.js";
import PhotoUpload from "../PhotoUpload.jsx";
import ImagePicker from "./ImagePicker.jsx";
import AutoTextarea from "../AutoTextarea.jsx";

function PersonRow({ person, clientId, onSave, onDelete, index, count, onMove, moving }) {
  const [editing, setEditing] = useState(false);
  const [libPickerOpen, setLibPickerOpen] = useState(false);
  const [form, setForm] = useState({ ...person });
  const [saving, setSaving] = useState(false);

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  async function save() {
    setSaving(true);
    try {
      await api.patch(`/people/${person.id}`, {
        first_name: form.first_name,
        last_initial: form.last_initial || null,
        display_name: form.display_name,
        relationship: form.relationship,
        role_in_life: form.role_in_life || null,
        notes: form.notes || null,
      });
      onSave({ ...person, ...form });
      setEditing(false);
    } catch (e) {
      alert(e.body?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!confirm(`Remove "${person.display_name}"?`)) return;
    await api.delete(`/people/${person.id}`);
    onDelete(person.id);
  }

  if (!editing) {
    return (
      <div style={styles.row}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          <PhotoUpload
            currentUrl={person.photo_media_id ? `/api/clients/${clientId}/media/${person.photo_media_id}?v=display` : null}
            uploadUrl={`/api/clients/${clientId}/media`}
            onUploaded={async (data) => {
              await api.patch(`/people/${person.id}`, { photo_media_id: data.id });
              onSave({ ...person, photo_media_id: data.id });
            }}
            size="sm"
          />
          <button
            style={{ fontSize: 9, color: "var(--color-accent)", background: "none", border: "none", cursor: "pointer", padding: 0, whiteSpace: "nowrap" }}
            onClick={() => setLibPickerOpen(true)}
          >
            Library
          </button>
          {libPickerOpen && (
            <ImagePicker
              clientId={clientId}
              // ImagePicker hands back the media OBJECT, not an id. This used
              // to send the whole object as photo_media_id, which the server
              // dropped — so a library pick never showed (C9).
              onSelect={async item => {
                const id = typeof item === "string" ? item : item?.id;
                if (!id) return;
                await api.patch(`/people/${person.id}`, { photo_media_id: id });
                onSave({ ...person, photo_media_id: id });
                setLibPickerOpen(false);
              }}
              onClose={() => setLibPickerOpen(false)}
            />
          )}
        </div>
        <div style={styles.rowMain}>
          <span style={styles.name}>{person.display_name}</span>
          <span style={styles.rel}>{person.relationship}</span>
          <SourceChip source={person.source} />
        </div>
        <div style={styles.actions}>
          {count > 1 && (
            <>
              <button type="button" style={styles.tiny} onClick={() => onMove(index, -1)}
                      disabled={moving || index === 0} aria-label={`Move ${person.display_name} up`}>↑</button>
              <button type="button" style={styles.tiny} onClick={() => onMove(index, 1)}
                      disabled={moving || index === count - 1} aria-label={`Move ${person.display_name} down`}>↓</button>
            </>
          )}
          <button style={styles.editBtn} onClick={() => setEditing(true)}>Edit</button>
          <button style={styles.deleteBtn} onClick={remove}>✕</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.editCard}>
      <div style={styles.twoCol}>
        <div style={styles.field}>
          <label style={styles.label}>Display name</label>
          <input style={styles.input} value={form.display_name} onChange={f("display_name")} />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Relationship</label>
          <input style={styles.input} value={form.relationship} onChange={f("relationship")} placeholder="e.g. mother, roommate" />
        </div>
      </div>
      <div style={styles.field}>
        <label style={styles.label}>Role in life</label>
        <AutoTextarea style={styles.textarea} value={form.role_in_life || ""} onChange={f("role_in_life")} rows={2} placeholder="What this person means to her day-to-day" />
      </div>
      <div style={styles.field}>
        <label style={styles.label}>Notes (optional)</label>
        <input style={styles.input} value={form.notes || ""} onChange={f("notes")} />
      </div>
      <div style={styles.editActions}>
        <button style={styles.saveBtn} onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
        <button style={styles.cancelBtn} onClick={() => { setForm({ ...person }); setEditing(false); }}>Cancel</button>
      </div>
    </div>
  );
}

function AddPersonForm({ clientId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_initial: "", display_name: "", relationship: "", role_in_life: "" });
  const [saving, setSaving] = useState(false);

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  async function add() {
    if (!form.display_name.trim() || !form.relationship.trim()) return;
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/sections/people/people`, {
        first_name: form.first_name,
        last_initial: form.last_initial || null,
        display_name: form.display_name,
        relationship: form.relationship,
        role_in_life: form.role_in_life || null,
      });
      onAdded();
      setForm({ first_name: "", last_initial: "", display_name: "", relationship: "", role_in_life: "" });
      setOpen(false);
    } catch (e) {
      alert(e.body?.error || "Add failed");
    } finally {
      setSaving(false);
    }
  }

  if (!open) return <button style={styles.addBtn} onClick={() => setOpen(true)}>+ Add person</button>;

  return (
    <div style={styles.editCard}>
      <div style={styles.twoCol}>
        <div style={styles.field}>
          <label style={styles.label}>Display name</label>
          <input style={styles.input} value={form.display_name} onChange={f("display_name")} placeholder="e.g. Stephanie O." />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Relationship</label>
          <input style={styles.input} value={form.relationship} onChange={f("relationship")} placeholder="e.g. mother" />
        </div>
      </div>
      <div style={styles.field}>
        <label style={styles.label}>Role in life</label>
        <AutoTextarea style={styles.textarea} value={form.role_in_life} onChange={f("role_in_life")} rows={2} placeholder="What this person means to her day-to-day" />
      </div>
      <div style={styles.editActions}>
        <button style={styles.saveBtn} onClick={add} disabled={saving || !form.display_name.trim()}>{saving ? "Adding…" : "Add person"}</button>
        <button style={styles.cancelBtn} onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

// How many people the play-through introduction names (the authoring policy's
// max_people, contract v3.4.2 §15). Kept in step with
// backend/app/services/authoring_policy.py.
export const INTRO_NAMES = 5;

export default function PeopleEditor({ clientId, initialPeople = [], onRefresh }) {
  const [people, setPeople] = useState(initialPeople);
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState(null);

  // Saved at once, the whole order in one request. On failure the list goes
  // back to how it was, so the screen never shows an order the server lacks.
  async function move(i, dir) {
    const j = i + dir;
    if (j < 0 || j >= people.length) return;
    const before = people;
    const next = [...people];
    [next[i], next[j]] = [next[j], next[i]];
    setPeople(next);
    setMoving(true);
    setMoveError(null);
    try {
      await api.put(`/clients/${clientId}/sections/people/order`, { person_ids: next.map(p => p.id) });
    } catch (e) {
      setPeople(before);
      setMoveError(e.body?.error || "Couldn't save the new order");
    } finally {
      setMoving(false);
    }
  }

  return (
    <div style={styles.root}>
      <h3 style={styles.heading}>People</h3>
      {people.length > 1 && (
        <p style={styles.hint}>
          The intro stream names the first {INTRO_NAMES}; move people up or down to choose who.
        </p>
      )}
      {moveError && <p style={styles.error}>{moveError}</p>}
      {people.map((p, i) => (
        <PersonRow key={p.id} person={p} clientId={clientId}
          index={i} count={people.length} onMove={move} moving={moving}
          onSave={updated => setPeople(prev => prev.map(x => x.id === updated.id ? updated : x))}
          onDelete={id => setPeople(prev => prev.filter(x => x.id !== id))}
        />
      ))}
      <AddPersonForm clientId={clientId} onAdded={onRefresh} />
    </div>
  );
}

const styles = {
  hint: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", margin: 0 },
  error: { fontSize: "var(--text-xs)", color: "var(--color-critical)", margin: 0 },
  tiny: { font: "inherit", fontSize: "var(--text-xs)", width: 24, height: 24, border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", background: "none", color: "var(--color-text-secondary)", cursor: "pointer", padding: 0 },
  root: { display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  heading: { fontSize: "var(--text-xs)", fontWeight: "var(--weight-semibold)", textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--color-text-tertiary)", paddingBottom: "var(--space-2)", borderBottom: "1px solid var(--color-border)" },
  row: { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-3) var(--space-4)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-4)" },
  rowMain: { display: "flex", alignItems: "center", gap: "var(--space-3)", flexWrap: "wrap", minWidth: 0 },
  name: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)" },
  rel: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
  actions: { display: "flex", gap: "var(--space-2)" },
  editBtn: { fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "2px var(--space-3)", cursor: "pointer" },
  deleteBtn: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", background: "none", border: "none", cursor: "pointer", padding: "2px var(--space-2)" },
  editCard: { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: "var(--space-5)", display: "flex", flexDirection: "column", gap: "var(--space-4)" },
  twoCol: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" },
  field: { display: "flex", flexDirection: "column", gap: "var(--space-1)" },
  label: { fontSize: "var(--text-xs)", fontWeight: "var(--weight-medium)", color: "var(--color-text-tertiary)" },
  input: { fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", background: "var(--color-bg-base)", color: "var(--color-text-primary)", width: "100%" },
  textarea: { fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", background: "var(--color-bg-base)", color: "var(--color-text-primary)", width: "100%", resize: "vertical", fontFamily: "inherit" },
  editActions: { display: "flex", gap: "var(--space-3)" },
  saveBtn: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "var(--color-accent)", color: "var(--color-text-on-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  cancelBtn: { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer" },
  addBtn: { fontSize: "var(--text-sm)", color: "var(--color-accent)", background: "var(--color-accent-subtle)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer", alignSelf: "flex-start" },
};
